import { useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import {
  PREVIEW_HEADER_INLINE_LOGO_MAX_WIDTH_RATIO,
  PREVIEW_HEADER_NAME_MAX_LINES,
  PREVIEW_HEADER_WIDE_LOGO_ASPECT_RATIO,
} from '../config';

type PreviewHeaderLayout = {
  isStacked: boolean;
  logoWidth: number | undefined;
};

const initialLayout: PreviewHeaderLayout = {
  isStacked: true,
  logoWidth: undefined,
};

export const usePreviewHeaderLayout = ({
  logoSrc,
  organizationName,
}: {
  logoSrc: string;
  organizationName: string | undefined;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const frameRef = useRef(0);
  const [layout, setLayout] = useState(initialLayout);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const logo = logoRef.current;
    const title = titleRef.current;

    setLayout(initialLayout);

    if (!section || !logo || !title) {
      return;
    }

    const measure = () => {
      const { naturalWidth, naturalHeight, complete } = logo;

      if (!complete || !naturalWidth || !naturalHeight) {
        return;
      }

      const sectionStyle = getComputedStyle(section);
      const sectionWidth = parseFloat(sectionStyle.width);

      if (sectionWidth <= 0) {
        return;
      }

      const aspectRatio = naturalWidth / naturalHeight;
      const maxLogoHeight = parseFloat(getComputedStyle(logo).maxHeight);
      const stackedLogoWidth = Math.min(
        naturalWidth,
        maxLogoHeight * aspectRatio,
        sectionWidth,
      );
      const inlineLogoWidth = Math.min(
        stackedLogoWidth,
        sectionWidth * PREVIEW_HEADER_INLINE_LOGO_MAX_WIDTH_RATIO,
      );
      const titleWidth =
        sectionWidth - inlineLogoWidth - parseFloat(sectionStyle.columnGap);
      const wideLogoNeedsSpace =
        aspectRatio >= PREVIEW_HEADER_WIDE_LOGO_ASPECT_RATIO &&
        stackedLogoWidth > inlineLogoWidth;

      const horizontalTitle = title.cloneNode(true) as HTMLHeadingElement;

      horizontalTitle.setAttribute('aria-hidden', 'true');
      Object.assign(horizontalTitle.style, {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        insetInlineStart: '0',
        insetBlockStart: '0',
        overflowWrap: 'normal',
        width: `${Math.max(0, titleWidth)}px`,
      });
      section.appendChild(horizontalTitle);

      const lineHeight = parseFloat(
        getComputedStyle(horizontalTitle).lineHeight,
      );
      const titleFits =
        titleWidth > 0 &&
        horizontalTitle.scrollHeight <=
          lineHeight * PREVIEW_HEADER_NAME_MAX_LINES &&
        horizontalTitle.scrollWidth <= horizontalTitle.clientWidth;

      horizontalTitle.remove();

      const isStacked = wideLogoNeedsSpace || !titleFits;
      const logoWidth = isStacked ? stackedLogoWidth : inlineLogoWidth;

      setLayout((previous) =>
        previous.isStacked === isStacked && previous.logoWidth === logoWidth
          ? previous
          : { isStacked, logoWidth },
      );
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(measure);
    };

    const measureBeforePrint = () => flushSync(measure);
    const observer = new ResizeObserver(scheduleMeasure);

    observer.observe(section);
    logo.addEventListener('load', measure);
    document.fonts.addEventListener('loadingdone', measure);
    window.addEventListener('beforeprint', measureBeforePrint);
    window.addEventListener('afterprint', scheduleMeasure);
    measure();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(frameRef.current);
      logo.removeEventListener('load', measure);
      document.fonts.removeEventListener('loadingdone', measure);
      window.removeEventListener('beforeprint', measureBeforePrint);
      window.removeEventListener('afterprint', scheduleMeasure);
    };
  }, [logoSrc, organizationName]);

  return { ...layout, sectionRef, logoRef, titleRef };
};
